import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useProperties } from '@/hooks/useProperties';
import type { Property } from '@/services/properties';

function createGalleryImages(property: Property) {
  const ownPhotos = property.photos.length > 0
    ? property.photos.map((photo) => photo.url)
    : [property.image].filter(Boolean);

  return ownPhotos.length > 0 ? ownPhotos : [''];
}

export default function ImovelDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const { properties: allProperties } = useProperties();

  const property = useMemo(() => {
    return allProperties.find((p) => p.id === id);
  }, [allProperties, id]);

  const relatedProperties = useMemo(() => {
    if (!property) return [];
    return allProperties
      .filter((p) => p.id !== property.id && (p.city === property.city || p.type === property.type))
      .slice(0, 3);
  }, [allProperties, property]);

  const galleryImages = useMemo(
    () => (property ? createGalleryImages(property) : []),
    [property]
  );

  useEffect(() => {
    setActiveImage(0);
  }, [property?.id]);

  const showPreviousImage = () => {
    setActiveImage((current) => (current - 1 + galleryImages.length) % galleryImages.length);
  };

  const showNextImage = () => {
    setActiveImage((current) => (current + 1) % galleryImages.length);
  };

  if (!property) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="px-4 md:px-8 py-20 text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-background-400">
            <i className="ri-home-smile-line text-3xl"></i>
          </span>
          <h1 className="text-2xl font-bold text-foreground-800 mb-2">Imóvel não encontrado</h1>
          <p className="text-sm text-foreground-500 mb-6">O imóvel que você procura não existe ou foi removido.</p>
          <button
            onClick={() => navigate('/imoveis')}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-xs"></i>
            </span>
            Ver todos os imóveis
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb & back */}
      <section className="bg-background-100 border-b border-background-200/70 py-4 px-4 md:px-8">
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate('/imoveis')}
            className="text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-1"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-xs"></i>
            </span>
            Todos os imóveis
          </button>
          <span className="text-background-400">/</span>
          <span className="text-foreground-800 font-medium">{property.title}</span>
        </div>
      </section>

      <div className="px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Property gallery */}
            <div className="mb-7">
              <div className="group relative rounded-lg overflow-hidden bg-background-200">
                {galleryImages[activeImage] ? (
                  <img
                    src={galleryImages[activeImage]}
                    alt={`${property.title} - foto ${activeImage + 1}`}
                    className="w-full h-72 md:h-[30rem] object-cover object-center transition-opacity duration-300"
                  />
                ) : (
                  <div className="w-full h-72 md:h-[30rem] flex items-center justify-center text-background-400">
                    <i className="ri-image-line text-4xl"></i>
                  </div>
                )}
                <span
                  className={`absolute top-4 left-4 px-3 py-1.5 rounded text-xs font-bold tracking-wider text-background-50 ${
                    property.tag === 'VENDA' ? 'bg-primary-500' : 'bg-secondary-500'
                  }`}
                >
                  {property.tag}
                </span>
                <span className="absolute right-4 top-4 rounded-full bg-secondary-950/75 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                  {activeImage + 1} / {galleryImages.length}
                </span>
                <button
                  type="button"
                  onClick={showPreviousImage}
                  aria-label="Ver foto anterior"
                  className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-secondary-900 shadow-lg transition hover:scale-105 hover:bg-white"
                >
                  <i className="ri-arrow-left-s-line text-2xl"></i>
                </button>
                <button
                  type="button"
                  onClick={showNextImage}
                  aria-label="Ver próxima foto"
                  className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-secondary-900 shadow-lg transition hover:scale-105 hover:bg-white"
                >
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </button>
              </div>

              <div className="mt-3 grid grid-cols-5 gap-2 md:gap-3">
                {galleryImages.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    onClick={() => setActiveImage(index)}
                    aria-label={`Abrir foto ${index + 1}`}
                    className={`overflow-hidden rounded-md border-2 transition-all ${
                      activeImage === index
                        ? 'border-primary-500 shadow-md'
                        : 'border-transparent opacity-75 hover:opacity-100'
                    }`}
                  >
                    {image ? (
                      <img
                        src={image}
                        alt=""
                        className="h-14 w-full object-cover sm:h-20 md:h-24"
                      />
                    ) : (
                      <div className="h-14 w-full flex items-center justify-center bg-background-200 text-background-400 sm:h-20 md:h-24">
                        <i className="ri-image-line"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Title & Location */}
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
                {property.title}
              </h1>
              <div className="flex items-center gap-1.5 text-foreground-600">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-map-pin-line text-sm"></i>
                </span>
                <span className="text-sm">{property.location}</span>
              </div>
            </div>

            {/* Price */}
            <div className="bg-primary-50 rounded-lg p-4 md:p-5 mb-6">
              <p className="text-2xl md:text-3xl font-bold text-primary-600">{property.price}</p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {property.bedrooms > 0 && (
                <div className="bg-background-100 rounded-lg p-4 text-center">
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-primary-500">
                    <i className="ri-hotel-bed-line text-xl"></i>
                  </span>
                  <p className="text-lg font-bold text-secondary-900">{property.bedrooms}</p>
                  <p className="text-xs text-foreground-600">Quartos</p>
                </div>
              )}
              {property.bathrooms > 0 && (
                <div className="bg-background-100 rounded-lg p-4 text-center">
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-primary-500">
                    <i className="ri-showers-line text-xl"></i>
                  </span>
                  <p className="text-lg font-bold text-secondary-900">{property.bathrooms}</p>
                  <p className="text-xs text-foreground-600">Banheiros</p>
                </div>
              )}
              {property.parking > 0 && (
                <div className="bg-background-100 rounded-lg p-4 text-center">
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-primary-500">
                    <i className="ri-car-line text-xl"></i>
                  </span>
                  <p className="text-lg font-bold text-secondary-900">{property.parking}</p>
                  <p className="text-xs text-foreground-600">Vagas</p>
                </div>
              )}
              <div className="bg-background-100 rounded-lg p-4 text-center">
                <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-primary-500">
                  <i className="ri-shape-line text-xl"></i>
                </span>
                <p className="text-lg font-bold text-secondary-900">{property.area}</p>
                <p className="text-xs text-foreground-600">m²</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4">Descrição</h2>
              <p className="text-base md:text-lg text-foreground-700 leading-8 md:leading-9">
                {property.description}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-background-100 rounded-lg border border-background-200/70 p-5 md:p-6 sticky top-24">
              <h3 className="text-base font-bold text-secondary-900 mb-4">
                Interessado neste imóvel?
              </h3>
              <p className="text-xs text-foreground-600 mb-5 leading-relaxed">
                Entre em contato com nossos corretores para mais informações ou agendar uma visita.
              </p>

              <a
                href={`https://wa.me/554991950002?text=Olá! Tenho interesse no imóvel: ${property.title} - ${property.location}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-4 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors mb-3"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line"></i>
                </span>
                Falar pelo WhatsApp
              </a>

              <a
                href="mailto:mauricio@grupobni.com.br"
                className="w-full flex items-center justify-center gap-2 bg-background-50 border border-background-300/60 text-foreground-800 px-4 py-3 rounded-md text-sm font-semibold hover:bg-background-200/70 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line"></i>
                </span>
                Enviar e-mail
              </a>

              <div className="mt-5 pt-5 border-t border-background-200/70">
                <p className="text-xs text-foreground-600 mb-2">Ou ligue diretamente:</p>
                <a
                  href="tel:+554991950002"
                  className="text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-xs"></i>
                  </span>
                  (49) 9 9195-0002
                </a>
              </div>
            </div>
          </aside>
        </div>

        {/* Related properties */}
        {relatedProperties.length > 0 && (
          <div className="mt-12 pt-10 border-t border-background-200/70">
            <h2 className="text-lg font-bold text-secondary-900 mb-6">
              Imóveis similares
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedProperties.map((p) => (
                <article
                  key={p.id}
                  onClick={() => navigate(`/imovel/${p.id}`)}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="relative h-44 overflow-hidden bg-background-200">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-background-400">
                        <i className="ri-image-line text-2xl"></i>
                      </div>
                    )}
                    <span
                      className={`absolute top-3 left-3 px-2.5 py-1 rounded text-[10px] font-bold tracking-wider text-background-50 ${
                        p.tag === 'VENDA' ? 'bg-primary-500' : 'bg-secondary-500'
                      }`}
                    >
                      {p.tag}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-[11px] text-foreground-600 mb-1">{p.location}</p>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{p.title}</h3>
                    <p className="text-sm font-bold text-primary-600">{p.price}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </main>
  );
}
